<template>
    <h2 class="mt-0">Avatars</h2>
    <fieldset class="mb-4 grid gap-4 border px-4 py-2">
        <legend>Custom avatar copy</legend>
        <div class="grid">
            <label for="firstName">First Name</label>
            <input
                v-model="firstNameCopy"
                id="firstName"
                class="rounded-sm"
                type="text"
            />
            <label
                class="pt-2"
                for="lastName"
            >
                Last Name
            </label>
            <input
                v-model="lastNameCopy"
                id="lastName"
                class="rounded-sm"
                type="text"
            />

            <label
                class="pt-2"
                for="icon"
            >
                Icon
            </label>
            <input
                v-model="iconCopy"
                id="icon"
                class="rounded-sm"
                type="text"
            />
            <label
                class="pt-2"
                for="institutionName"
            >
                Institution Name
            </label>
            <select
                v-model="institutionNameCopy"
                id="institutionName"
                class="rounded-sm"
            >
                <option
                    v-for="logo in Array.from(bankLogoRepositoryMap.keys())"
                    :key="logo"
                    :value="logo"
                >
                    {{ logo }}
                </option>
            </select>
        </div>
    </fieldset>

    <section class="flex flex-col items-start justify-between md:flex-row">
        <div class="grid grid-cols-5 place-items-center gap-4">
            <code>initials</code>
            <DsAvatar.Initials
                :firstName="firstNameCopy || 'Closing'"
                :lastName="lastNameCopy || (firstNameCopy ? '' : 'Lock')"
                size="sm"
            />
            <DsAvatar.Initials
                :firstName="firstNameCopy || 'Closing'"
                :lastName="lastNameCopy || (firstNameCopy ? '' : 'Lock')"
                size="md"
            />
            <DsAvatar.Initials
                :firstName="firstNameCopy || 'Closing'"
                :lastName="lastNameCopy || (firstNameCopy ? '' : 'Lock')"
                size="lg"
            />
            <DsAvatar.Initials
                :firstName="firstNameCopy || 'Closing'"
                :lastName="lastNameCopy || (firstNameCopy ? '' : 'Lock')"
                size="xl"
            />

            <code>icon</code>
            <DsAvatar.Icon
                :icon="iconCopy || 'fa-regular fa-user'"
                size="sm"
            />
            <DsAvatar.Icon
                :icon="iconCopy || 'fa-regular fa-user'"
                size="md"
            />
            <DsAvatar.Icon
                :icon="iconCopy || 'fa-regular fa-user'"
                size="lg"
            />
            <DsAvatar.Icon
                :icon="iconCopy || 'fa-regular fa-user'"
                size="xl"
            />
            <code>bank</code>
            <DsAvatar.Bank
                :logoUrl="bankLogoRepositoryMap.get(institutionNameCopy) ?? ''"
                size="sm"
            />
            <DsAvatar.Bank
                :logoUrl="bankLogoRepositoryMap.get(institutionNameCopy) ?? ''"
                size="md"
            />
            <DsAvatar.Bank
                :logoUrl="bankLogoRepositoryMap.get(institutionNameCopy) ?? ''"
                size="lg"
            />
            <DsAvatar.Bank
                :logoUrl="bankLogoRepositoryMap.get(institutionNameCopy) ?? ''"
                size="xl"
            />

            <code></code>
            <code>sm</code>
            <code>md</code>
            <code>lg</code>
            <code>xl</code>
        </div>

        <fieldset class="mb-4 grid w-1/2 gap-4 border px-4 py-2">
            <legend>Interactive Demo</legend>
            <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <label for="demoType">Type</label>
                    <select
                        v-model="demoType"
                        class="rounded-sm"
                    >
                        <option value="initials">Initials</option>
                        <option value="icon">Icon</option>
                        <option value="bank">Bank</option>
                    </select>
                </div>
                <div class="flex flex-col gap-2">
                    <label for="demoSize">Size</label>
                    <select
                        v-model="demoSize"
                        class="rounded-sm"
                    >
                        <option value="sm">Small</option>
                        <option value="md">Medium</option>
                        <option value="lg">Large</option>
                        <option value="xl">Extra Large</option>
                    </select>
                </div>
                <div
                    v-if="demoType === 'initials'"
                    class="flex flex-col gap-2"
                >
                    <label for="demoFirstName">First Name</label>
                    <input
                        v-model="demoFirstName"
                        class="rounded-sm"
                        placeholder="First Name"
                        type="text"
                    />
                </div>
                <div
                    v-if="demoType === 'initials'"
                    class="flex flex-col gap-2"
                >
                    <label for="demoLastName">Last Name</label>
                    <input
                        v-model="demoLastName"
                        class="rounded-sm"
                        placeholder="Last Name"
                        type="text"
                    />
                </div>
                <div
                    v-if="demoType === 'icon'"
                    class="flex flex-col gap-2"
                >
                    <label for="demoIcon">Icon</label>
                    <input
                        v-model="demoIcon"
                        class="rounded-sm"
                        placeholder="Icon"
                        type="text"
                    />
                </div>
                <div
                    v-if="demoType === 'bank'"
                    class="flex flex-col gap-2"
                >
                    <label for="demoInstitutionName">Institution Name</label>
                    <select
                        v-model="demoInstitutionName"
                        class="rounded-sm"
                    >
                        <option
                            v-for="logo in Array.from(bankLogoRepositoryMap.keys())"
                            :key="`${logo}`"
                            :value="logo"
                        >
                            {{ logo }}
                        </option>
                    </select>
                </div>
            </div>

            <DsAvatar.Initials
                v-if="demoType === 'initials'"
                :firstName="demoFirstName || 'Closing'"
                :lastName="demoLastName || (demoFirstName ? '' : 'Lock')"
                :size="demoSize"
            />
            <DsAvatar.Icon
                v-else-if="demoType === 'icon'"
                :icon="demoIcon"
                :size="demoSize"
            />
            <DsAvatar.Bank
                v-else-if="demoType === 'bank'"
                :logoUrl="bankLogoRepositoryMap.get(demoInstitutionName) ?? ''"
                :size="demoSize"
            />
        </fieldset>
    </section>
</template>

<script setup lang="ts">
import DsAvatar from '@/components/design-system/avatar';
import { type InstitutionName, bankLogoRepositoryMap } from '@/components/design-system/avatar/bank-logo-repository';
import type { DsAvatarProps } from '@ds/avatar/DsAvatarBase.vue';
import { ref } from 'vue';

const firstNameCopy = ref('Closing');
const lastNameCopy = ref('Lock');
const iconCopy = ref('fa-regular fa-user');
const institutionNameCopy = ref<InstitutionName>('Chase');

// Common props
const demoType = ref<DsAvatarProps['config']['type']>('initials');
const demoSize = ref<DsAvatarProps['config']['size']>('sm');

// Initials
const demoFirstName = ref('Closing');
const demoLastName = ref('Lock');

// Icon
const demoIcon = ref('fa-regular fa-user');

// Bank
const demoInstitutionName = ref<InstitutionName>('Chase');
</script>

<style lang="scss" scoped>
h2,
h3 {
    @apply my-4;
}
</style>
